/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/countrySelect.scss';

import {
    capitalize,
    ChangeFunction,
    ChangeKeyFunction,
    Checkbox,
    CloseButton,
    Loader,
    voidFunction,
    WithBackground
} from '@dvrd/dvr-controls';

import React, {CSSProperties, MouseEventHandler} from 'react';

import {BluemateButton} from '../../../index';

import FixedSizeChecklist from '../../../../misc/fixedSizeChecklist';
import {useTranslation} from 'react-i18next';

interface Props {
    onSelectCountry: ChangeKeyFunction;
    onSelectAll: ChangeFunction;
    onSubmit: MouseEventHandler;
    onClose: MouseEventHandler;
    allSelected: boolean;
    loading: boolean;
    active: boolean;
    countries: string[];
    selectedCountries: Set<string>;
}

const languageKey = 'controls.countrySelect';

export default function CountrySelect(props: Props) {
    const {
        selectedCountries,
        active,
        onClose,
        onSelectCountry,
        onSelectAll,
        allSelected,
        loading,
        onSubmit,
        countries
    } = props;
    const {t} = useTranslation();

    function renderBody() {
        const selectable = countries.filter((country: string) => !selectedCountries.has(country));
        return (
            <div className='body-container'>
                <Loader active={loading} />
                <Checkbox
                    checked={allSelected}
                    onCheck={onSelectAll}
                    label={t(`${languageKey}.all`, 'Alle') + ' ' + t(`${languageKey}.countries`, 'landen')}
                    className='grid-col-3'
                />
                <div className='col'>
                    <label className='col-title'>
                        {t(`${languageKey}.available`, 'Beschikbare')} {t(`${languageKey}.countries`, 'landen')}
                    </label>
                    <FixedSizeChecklist items={selectable} renderRow={renderCountry(false)} disabled={loading} />
                </div>
                <div className='divider' />
                <div className='col'>
                    <label className='col-title'>
                        {t(`${languageKey}.selected`, 'Geselecteerde')} {t(`${languageKey}.countries`, 'landen')}
                        {allSelected ? ` (${t(`${languageKey}.all`, 'alle').toLowerCase()})` : ''}
                    </label>
                    <FixedSizeChecklist
                        items={Array.from(selectedCountries)}
                        renderRow={renderCountry(true)}
                        disabled={loading || allSelected}
                    />
                </div>
            </div>
        );
    }

    function renderCountry(selected: boolean) {
        return function (country: string, index: number, style: CSSProperties) {
            return (
                <div style={style} onClick={onSelectCountry(country)} className='list-row'>
                    <Checkbox key={index} checked={selected} onCheck={voidFunction} label={country} />
                </div>
            );
        };
    }

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='country-select default-popup'>
                <div className='header-container'>
                    <label className='header-title'>
                        {capitalize(t(`${languageKey}.selectCountries`, 'Landen selecteren'))}
                    </label>
                    <CloseButton onClick={onClose} />
                </div>
                {renderBody()}
                <div className='footer-container'>
                    <BluemateButton onClick={onSubmit} label={t(`${languageKey}.done`, 'Klaar')} disabled={loading} />
                </div>
            </div>
        </WithBackground>
    );
}
